import {
    compositeArgs,
    compositeClause,
    compositeFromArgs,
    day,
    inArgumentOrder,
    unquoted,
} from 'components/tables/list-table/compositeFilter';
import { emptyTextClause, notComparator } from 'components/tables/list-table/filterUtil';
import { getSelectorKey } from 'components/tables/list-table/getSelectorKey';
import {
    CompositePartForm,
    isEmptinessOption,
    MainFilter,
    MainFilterForm,
    MainFilterInForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    TextMatch,
} from 'components/tables/list-table/ListTableTypes';
import isEqual from 'lodash/isEqual';
import { ClassifierData } from 'types/classifiers';
import { EmployeeListItemMinDto } from 'types/employees';
import {
    MarketingProfileCreateUpdateRequest,
    MarketingProfileFilterClause,
    MarketingProfileFilterCondition,
    MarketingProfileFilterCriteria,
    MarketingProfileFilterField,
} from 'types/marketingProfiles';
import { getClassifierText } from 'utils/getTranslations';
import { isNumeric } from 'utils/number';

type EmployeeOption = Pick<EmployeeListItemMinDto, 'id' | 'name'>;
type ClassifierOption = { value: string; label: string; unsupported?: boolean };

const NAME_ERROR_MESSAGES = new Set([
    'Marketing profile name already exists',
    'Marketing profile name is required',
    'name must not be blank',
    'name size must be between 0 and 255',
]);

export function mergeEmployeeOptions(options: EmployeeOption[], selected: EmployeeOption[]): EmployeeOption[] {
    const byId = new Map<number, EmployeeOption>();
    [...selected, ...options].forEach((option) => byId.set(option.id, option));
    return Array.from(byId.values());
}

export function buildClassifierOptions(
    classifier: ClassifierData | undefined,
    selectedCodes: string[]
): ClassifierOption[] {
    const byValue = new Map<string, ClassifierOption>(
        (classifier?.values ?? []).map((option) => [
            option.code,
            { value: option.code, label: getClassifierText(classifier, option.code) },
        ])
    );

    selectedCodes.forEach((code) => {
        if (!byValue.has(code)) {
            byValue.set(code, { value: code, label: `Unsupported (${code})`, unsupported: true });
        }
    });

    return Array.from(byValue.values()).sort((a, b) => {
        if (a.unsupported && !b.unsupported) return 1;
        if (!a.unsupported && b.unsupported) return -1;
        return a.label.localeCompare(b.label);
    });
}

/** One filter of the profile panel: the panel's control and the clause it saves as. */
export type MarketingProfileFilterDef = { field: MarketingProfileFilterField; filter: MainFilter };

const clause = (
    filter: MarketingProfileFilterField,
    condition: MarketingProfileFilterCondition,
    values?: (string | number)[]
): MarketingProfileFilterClause => ({ filter, condition, ...(values ? { values: values.map(String) } : {}) });

/**
 * The version 1 fields as the clauses the panel opens them as, a field with no value left out: the backend
 * compiles a version 1 profile through the same clauses, so a profile re-saved as clauses matches the same persons.
 */
export function legacyClauses(c: MarketingProfileFilterCriteria): MarketingProfileFilterClause[] {
    const lists: [MarketingProfileFilterField, (string | number)[] | undefined, boolean | undefined][] = [
        ['BUSINESS_CONTACT_OF', c.businessContactEmployeeIds, undefined],
        ['MAILING_PREFERENCE', c.mailingPreferenceTypeCodes, c.excludeMailingPreferences],
        ['MARKETING_AREA', c.marketingAreaTypeCodes, undefined],
        ['PREFERRED_MAILING_LANGUAGE', c.preferredMailingLanguageTypeCodes, undefined],
        ['JURISDICTION', c.countryTypeCodes, undefined],
        ['EMPLOYER_COUNTRY', c.employerCountryTypeCodes, c.excludeEmployerCountries],
        ['EMPLOYER_BUSINESS_AREA', c.employerBusinessAreaTypeCodes, c.excludeEmployerBusinessAreas],
        ['EMPLOYER_SEGMENT', c.employerSegmentTypeCodes, c.excludeEmployerSegments],
        ['EMPLOYER_SERVICE_LEVEL', c.employerServiceLevelTypeCodes, c.excludeEmployerServiceLevels],
        ['EMPLOYER_KEYWORD', c.employerKeywordTypeCodes, c.excludeEmployerKeywords],
    ];
    const toggles: [MarketingProfileFilterField, MarketingProfileFilterCondition, boolean | undefined][] = [
        ['EXCLUDE_EMPLOYED_BY_INTERNAL_CLIENT', 'IS', c.excludeEmployedByInternalClient],
        ['EMAIL', 'IS_NOT_EMPTY', c.excludeWithoutEmail],
        ['EXCLUDE_PERSONA_NON_GRATA', 'IS', c.excludePersonaNonGrata],
    ];
    return [
        ...lists
            .filter(([, values]) => values?.some((value) => String(value).trim()))
            .map(([field, values, exclude]) => clause(field, exclude ? 'IS_NOT' : 'IS', values)),
        ...toggles.filter(([, , on]) => on === true).map(([field, condition]) => clause(field, condition)),
    ];
}

const clauseKey = (c: MarketingProfileFilterClause) =>
    JSON.stringify([
        c.filter,
        c.condition,
        c.values,
        c.from,
        c.to,
        c.atLeast,
        c.months,
        c.direction,
        c.amountFrom,
        c.amountTo,
        c.period,
    ]);

/**
 * The criteria as a save writes them: every filter a clause (a version 1 profile's fields converted), in a fixed
 * order, so two criteria that filter alike compare equal.
 */
export function normalizeFilterCriteria(criteria: MarketingProfileFilterCriteria): MarketingProfileFilterCriteria {
    const clauses = [...legacyClauses(criteria), ...(criteria.clauses ?? [])]
        .map((c) => ({ ...c, values: c.values?.map((value) => value.trim()).filter(Boolean) }))
        .map(
            (c) =>
                Object.fromEntries(
                    Object.entries(c).filter(
                        ([, value]) => value != null && value !== '' && (!Array.isArray(value) || value.length)
                    )
                ) as unknown as MarketingProfileFilterClause
        )
        .toSorted((a, b) => clauseKey(a).localeCompare(clauseKey(b)));
    return { clauses };
}

export const hasAnyCriteria = (criteria: MarketingProfileFilterCriteria) =>
    (normalizeFilterCriteria(criteria).clauses?.length ?? 0) > 0;

export const isSameCriteria = (a: MarketingProfileFilterCriteria, b: MarketingProfileFilterCriteria) =>
    isEqual(normalizeFilterCriteria(a), normalizeFilterCriteria(b));

export function buildMarketingProfileRequest(
    name: string,
    criteria: MarketingProfileFilterCriteria,
    selectedBusinessContacts?: EmployeeOption[]
): MarketingProfileCreateUpdateRequest {
    const mergedCriteria = selectedBusinessContacts?.length
        ? { ...criteria, businessContactEmployeeIds: selectedBusinessContacts.map((e) => e.id) }
        : criteria;
    return {
        name: name.trim(),
        filterCriteria: normalizeFilterCriteria(mergedCriteria),
    };
}

export function isMarketingProfileNameError(message?: string | null): boolean {
    return typeof message === 'string' && NAME_ERROR_MESSAGES.has(message);
}

const TEXT_CONDITIONS: Record<TextMatch, MarketingProfileFilterCondition> = {
    any: 'CONTAINS_ANY',
    all: 'CONTAINS_ALL',
    none: 'CONTAINS_NONE',
};

/** The yes / no filters of a value recorded or not: Yes is `true`, No `false`. */
const PRESENCE_FIELDS = new Set<MarketingProfileFilterField>(['EMAIL', 'PHONE', 'EMPLOYER_ADDRESS']);

/** The clause fields a composite filter's part fills: its selector names the field, `<selector>From` / `…To` a range's. */
function partFields(part: CompositePartForm): Partial<MarketingProfileFilterClause> {
    switch (part.type) {
        case 'multiselect':
            return { values: part.selected.map((option) => unquoted(option.value)) };
        case 'daterange':
            return { ...(part.from ? { from: day(part.from) } : {}), ...(part.to ? { to: day(part.to) } : {}) };
        case 'interval': {
            const amount = (bound: number | null) => (isNumeric(String(bound)) ? Number(bound) : null);
            return { [`${part.selector}From`]: amount(part.from), [`${part.selector}To`]: amount(part.to) };
        }
        case 'number':
            return { [part.selector]: part.value };
        case 'dropdown':
            return { [part.selector]: part.selected?.value ?? null };
        case 'checkboxgroup':
            return { [part.selector]: part.filters.find((option) => option.isSelected)?.argument ?? null };
    }
}

/** A composite filter's clause as its part arguments, in the order `compositeFromArgs` reads them. */
function partArgs(filter: Extract<MainFilter, { type: 'composite' }>, c: MarketingProfileFilterClause): string[] {
    const field = (key: string) => (c as unknown as Record<string, string | number | null | undefined>)[key];
    const text = (value: string | number | null | undefined) => (value == null ? '' : String(value));
    return inArgumentOrder(filter.parts).flatMap((part) => {
        switch (part.type) {
            case 'daterange':
                return [text(c.from), text(c.to)];
            case 'interval':
                return [text(field(`${part.selector}From`)), text(field(`${part.selector}To`))];
            case 'number':
            case 'dropdown':
                return [text(field(part.selector))];
            case 'multiselect':
                return c.values ?? [];
            case 'checkboxgroup':
                return field(part.selector) ? [text(field(part.selector))] : [];
        }
    });
}

/** The clause a panel control's state saves as, or none when the control filters nothing. */
function clauseOf(field: MarketingProfileFilterField, f: MainFilterInForm): MarketingProfileFilterClause | undefined {
    switch (f.type) {
        case 'multiselect':
        case 'dynamicmultiselect': {
            if (f.selected.some((option) => option.value === NOT_NULL_FILTER_SENTINEL)) {
                return clause(field, 'IS_NOT_EMPTY');
            }
            const values = f.selected
                .filter((option) => !isEmptinessOption(option))
                .map((option) => unquoted(option.value));
            if (!values.length) {
                return f.selected.some((option) => option.value === NULL_FILTER_SENTINEL)
                    ? clause(field, 'IS_EMPTY')
                    : undefined;
            }
            return clause(field, f.isExcluding ? 'IS_NOT' : 'IS', values);
        }
        case 'text': {
            const terms = (Array.isArray(f.value) ? f.value : f.value ? [f.value] : []).filter((term) => term.trim());
            if (!terms.length) {
                if (f.isEmpty) return clause(field, 'IS_EMPTY');
                return f.isNotEmpty ? clause(field, 'IS_NOT_EMPTY') : undefined;
            }
            return clause(field, TEXT_CONDITIONS[f.match ?? 'any'], terms);
        }
        case 'checkboxgroup': {
            const picked = f.filters.filter((option) => option.isSelected && !option.isNo);
            if (!picked.length) return undefined;
            if (PRESENCE_FIELDS.has(field)) {
                return clause(field, picked[0].argument === 'false' ? 'IS_EMPTY' : 'IS_NOT_EMPTY');
            }
            if (field === 'OPPONENT') {
                return clause(
                    field,
                    'IS',
                    picked.map((option) => (option.argument === 'true' ? 'IN_PROGRESS' : option.argument))
                );
            }
            return field === 'INTERNAL_CLIENT'
                ? clause(
                      field,
                      'IS',
                      picked.map((option) => option.argument)
                  )
                : clause(field, 'IS');
        }
        case 'daterange':
        case 'offset-date-time-range':
            if (!f.from && !f.to) return undefined;
            return {
                filter: field,
                condition: 'IS',
                ...(f.from ? { from: day(f.from) } : {}),
                ...(f.to ? { to: day(f.to) } : {}),
            };
        case 'date':
        case 'offset-date-time':
            return f.value ? { filter: field, condition: 'IS', to: day(f.value) } : undefined;
        case 'number':
            // The panel's one number filter, the months of "No BD effort in the last N months".
            return f.value != null ? { ...clause(field, 'IS'), months: f.value } : undefined;
        case 'composite':
            return compositeArgs(f)
                ? f.parts.reduce<MarketingProfileFilterClause>(
                      (c, part) => ({ ...c, ...partFields(part) }),
                      clause(field, f.isExcluding ? 'IS_NOT' : 'IS')
                  )
                : undefined;
        default:
            return undefined;
    }
}

/**
 * The panel's state as the criteria a save writes. A clause of `base` whose filter the panel does not draw (a gate
 * off, as one active office or the title setting) is kept as saved: the user could not see it to remove it.
 */
export function extractFilterCriteriaFromMainFilterState(
    state: MainFilterForm,
    defs: MarketingProfileFilterDef[],
    base: MarketingProfileFilterCriteria = {}
): MarketingProfileFilterCriteria {
    const clauses = defs.flatMap(({ field, filter }) => {
        const form = state[getSelectorKey(filter as MainFilterInForm)];
        const c = form ? clauseOf(field, form) : undefined;
        return c ? [c] : [];
    });
    const drawn = new Set(defs.map(({ field }) => field));
    const kept = (normalizeFilterCriteria(base).clauses ?? []).filter((c) => !drawn.has(c.filter));
    return normalizeFilterCriteria({ clauses: [...clauses, ...kept] });
}

/** A clause as the panel's own filter text, which the list reads its controls' state back from. */
function panelClause(filter: MainFilter, c: MarketingProfileFilterClause): string | undefined {
    const { selector, comperator } = filter;
    const values = c.values ?? [];
    switch (filter.type) {
        case 'multiselect':
        case 'dynamicmultiselect': {
            const { emptyComparator } = filter;
            if (c.condition === 'IS_EMPTY')
                return `${selector}${emptyComparator ?? '=isnull='}${emptyComparator ? 'false' : 'true'}`;
            if (c.condition === 'IS_NOT_EMPTY') return `${selector}${emptyComparator ?? '=nn='}true`;
            const args = values
                .map((value) => (filter.type === 'multiselect' || /^\d+$/.test(value) ? value : `"${value}"`))
                .join(',');
            if (c.condition !== 'IS_NOT') return `${selector}${comperator ?? '=in='}(${args})`;
            return `${selector}${comperator ? notComparator(comperator) : '=out='}(${args})`;
        }
        case 'text': {
            const { emptyComparator } = filter;
            if (c.condition === 'IS_EMPTY') {
                return emptyComparator ? `${selector}${emptyComparator}false` : emptyTextClause(selector);
            }
            if (c.condition === 'IS_NOT_EMPTY') return emptyComparator && `${selector}${emptyComparator}true`;
            const comparator = comperator ?? '=lk=';
            const prefix = `${selector}${c.condition === 'CONTAINS_NONE' ? notComparator(comparator) : comparator}`;
            const terms = values.map((term) => `"${term}"`);
            return c.condition === 'CONTAINS_ALL'
                ? terms.map((term) => `${prefix}(${term})`).join(';')
                : `${prefix}(${terms.join(',')})`;
        }
        case 'checkboxgroup': {
            const args =
                c.filter === 'OPPONENT'
                    ? values.map((value) => (value === 'IN_PROGRESS' ? 'true' : value))
                    : c.filter === 'INTERNAL_CLIENT'
                      ? values
                      : PRESENCE_FIELDS.has(c.filter)
                        ? [c.condition === 'IS_EMPTY' ? 'false' : 'true']
                        : filter.filters.slice(0, 1).map((option) => option.argument);
            return comperator && !filter.multiple
                ? `${selector}${comperator}${args.join(',')}`
                : `${selector}${comperator ?? '=in='}(${args.join(',')})`;
        }
        case 'offset-date-time-range':
            if (comperator) return `${selector}${comperator}(${c.from ?? '""'}${c.to ? `,${c.to}` : ''})`;
            return [c.from && `${selector}=ge=${c.from}`, c.to && `${selector}=le=${c.to}`].filter(Boolean).join(';');
        case 'daterange':
            return [c.from && `${selector}=ge=${c.from}`, c.to && `${selector}=le=${c.to}`].filter(Boolean).join(';');
        case 'date':
            return c.to ? `${selector}${comperator ?? '=in='}${c.to}` : undefined;
        case 'number':
            return c.months != null ? `${selector}${comperator ?? '=ge='}${c.months}` : undefined;
        case 'composite':
            return compositeClause(compositeFromArgs(filter, partArgs(filter, c), c.condition === 'IS_NOT'));
        default:
            return undefined;
    }
}

/** The saved criteria as the panel's initial filter text, through the clauses a version 1 profile opens as. */
export function buildInitialSearchParams(
    criteria: MarketingProfileFilterCriteria,
    defs: MarketingProfileFilterDef[]
): string | undefined {
    const fragments = (normalizeFilterCriteria(criteria).clauses ?? []).flatMap((c) => {
        const def = defs.find(({ field }) => field === c.filter);
        const fragment = def ? panelClause(def.filter, c) : undefined;
        return fragment ? [fragment] : [];
    });
    return fragments.length ? `mf=${fragments.join(';')}` : undefined;
}
